import React from "react";

import SmartImage from "../../../components/SmartImage";
import { formatMoney } from "../detailMappers";

export const StarRating = ({ rating, total, compact = false }) => {
  if (!total) return <span className="sy-rating sy-rating-empty">No ratings yet</span>;

  return (
    <span className="sy-rating">
      <span aria-hidden="true">★</span>
      {rating.toFixed(1)}
      {!compact && <i>({total})</i>}
      <span className="sy-visually-hidden">
        {` out of 5 from ${total} ${total === 1 ? "rating" : "ratings"}`}
      </span>
    </span>
  );
};

export const StarRow = ({ rating }) => {
  const filled = Math.round(rating);

  return (
    <span className="sy-star-row" aria-label={`${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map((step) => (
        <span
          key={step}
          aria-hidden="true"
          className={step <= filled ? "is-filled" : ""}
        >
          ★
        </span>
      ))}
    </span>
  );
};

export const PriceBlock = ({ price, originalPrice, currency, perKg, align }) => (
  <span className={`sy-price${align ? ` sy-price-${align}` : ""}`}>
    {Number.isFinite(originalPrice) && originalPrice > price && (
      <s>
        <span className="sy-visually-hidden">Was </span>
        {formatMoney(originalPrice, currency)}
      </s>
    )}

    <b>{formatMoney(price, currency)}</b>

    {Number.isFinite(perKg) && (
      <span className="sy-price-unit">{formatMoney(perKg, currency)} per Kg</span>
    )}
  </span>
);

export const AvailabilityBanner = ({ notice }) => {
  if (!notice) return null;

  return (
    <p className="sy-availability-banner" role="status">
      <span aria-hidden="true">!</span>
      {notice.short}
    </p>
  );
};

export const SpecList = ({ items }) => {
  const rows = (items || []).filter((row) => row.value);

  if (!rows.length) return null;

  return (
    <dl className="sy-spec-list">
      {rows.map((row) => (
        <div className="sy-spec-item" key={row.label}>
          <dt>{row.label}</dt>
          <dd>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
};

export const SupplierAvatar = ({ name, logoUrl, size = 42 }) => (
  <span className="sy-avatar" style={{ "--sy-avatar-size": `${size}px` }}>
    {logoUrl ? (
      <SmartImage src={logoUrl} alt="" width="100%" height="100%" />
    ) : (
      <span aria-hidden="true">{(name || "S").slice(0, 1).toUpperCase()}</span>
    )}
  </span>
);

export const PinIcon = () => (
  <svg className="sy-pin" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M8 1.9c2.2 0 4 1.8 4 4 0 2.9-4 8.2-4 8.2S4 8.8 4 5.9c0-2.2 1.8-4 4-4Z" />
    <circle cx="8" cy="5.9" r="1.5" />
  </svg>
);

export const QuantityStepper = ({ value, disabled, max = 10, onChange }) => (
  <div className="sy-qty">
    <button
      type="button"
      className="sy-qty-btn"
      aria-label="Decrease quantity"
      disabled={disabled || value <= 1}
      onClick={() => onChange(value - 1)}
    >
      <svg viewBox="0 0 20 20" aria-hidden="true">
        <path d="M5 10h10" />
      </svg>
    </button>

    <span className="sy-qty-value" aria-live="polite">
      {value}
    </span>

    <button
      type="button"
      className="sy-qty-btn"
      aria-label="Increase quantity"
      disabled={disabled || value >= max}
      onClick={() => onChange(value + 1)}
    >
      <svg viewBox="0 0 20 20" aria-hidden="true">
        <path d="M10 5v10M5 10h10" />
      </svg>
    </button>
  </div>
);
